<template>
  <div class="dashboard">
    <div v-if="loadingCampaigns" class="dashboard-state">
      <n-spin size="large" />
    </div>
    <n-empty
      v-else-if="campaigns.length === 0"
      description="Nenhuma campanha ainda"
      class="dashboard-state"
      data-test="dashboard-empty"
    >
      <template #extra>
        <n-button type="primary" @click="$router.push('/form')">
          Criar primeira campanha
        </n-button>
      </template>
    </n-empty>
    <div v-else class="campaigns-grid">
      <div
        v-for="campaign in campaigns"
        :key="campaign._id"
        class="campaign-card"
        data-test="campaign-card"
      >
        <header class="campaign-header">
          <h1>{{ campaign.name }}</h1>
          <button
            class="edit-btn"
            title="Adicionar estratégias"
            @click="editCampaign(campaign._id)"
          >
            ✏️
          </button>
        </header>

        <div class="strategies-container">
          <div
            v-for="strategy in campaign.strategies"
            :key="strategy._id"
            class="strategy-tile"
            data-test="strategy-tile"
          >
            <div class="strategy-header">
              <h3>{{ getStrategyTitle(strategy.type) }}</h3>
              <div class="metric-badge">
                <span class="metric-value" data-test="traffic-count">{{
                  trafficFor(strategy._id)
                }}</span>
                <span class="metric-label">Acessos</span>
              </div>
            </div>

            <div class="strategy-destination" :title="strategyTarget(strategy)">
              {{ strategyTarget(strategy) }}
            </div>

            <div class="strategy-actions">
              <button
                class="copy-url-btn"
                data-test="open-link-modal"
                @click="openLinkModal(campaign, strategy)"
              >
                Link + QR
              </button>
            </div>
          </div>
        </div>

        <button
          class="add-strategy-btn"
          @click="showAddStrategyModal(campaign._id)"
        >
          <span class="plus-icon">+</span>
          <span>Adicionar Estratégia</span>
        </button>
      </div>
    </div>

    <!-- Tracking link + QR modal -->
    <div v-if="showUtmModal" class="modal-overlay" @click="closeUtmModal">
      <div class="modal-content" @click.stop>
        <h3>Link de Rastreamento</h3>
        <form @submit.prevent="generateTrackingUrl">
          <div class="form-group">
            <label for="utm-source">Origem (utm_source):</label>
            <input
              id="utm-source"
              v-model="utmParams.utm_source"
              type="text"
              placeholder="Ex: facebook, google, email"
            />
          </div>

          <div class="form-group">
            <label for="utm-medium">Meio (utm_medium):</label>
            <input
              id="utm-medium"
              v-model="utmParams.utm_medium"
              type="text"
              placeholder="Ex: cpc, banner, newsletter"
            />
          </div>

          <div class="form-group">
            <label for="utm-campaign">Campanha (utm_campaign):</label>
            <input
              id="utm-campaign"
              v-model="utmParams.utm_campaign"
              type="text"
              placeholder="Ex: promocao_verao"
            />
          </div>

          <div class="form-group">
            <label for="utm-content">Conteúdo (utm_content):</label>
            <input
              id="utm-content"
              v-model="utmParams.utm_content"
              type="text"
              placeholder="Ex: banner_topo, link_texto"
            />
          </div>

          <div class="form-group">
            <label for="utm-term">Termo (utm_term):</label>
            <input
              id="utm-term"
              v-model="utmParams.utm_term"
              type="text"
              placeholder="Ex: palavras-chave"
            />
          </div>

          <div v-if="generatedUrl" class="generated-url">
            <label>URL Gerada:</label>
            <div class="url-display">
              <input
                v-model="generatedUrl"
                readonly
                data-test="generated-url"
              />
              <button type="button" class="copy-btn" @click="copyGeneratedUrl">
                Copiar
              </button>
            </div>
            <div class="qr-wrapper">
              <QrCodeCard
                :url="generatedUrl"
                :file-name="`${utmParams.utm_campaign || 'campanha'}-qr`"
              />
            </div>
          </div>

          <div class="modal-actions">
            <button type="button" class="cancel-btn" @click="closeUtmModal">
              Fechar
            </button>
            <button type="submit" class="save-btn" data-test="generate-url">
              Gerar URL
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Add Strategy Modal -->
    <div v-if="showModal" class="modal-overlay" @click="closeModal">
      <div class="modal-content" @click.stop>
        <h3>Adicionar Nova Estratégia</h3>
        <form @submit.prevent="addNewStrategy">
          <div class="form-group">
            <label for="strategy-type">Tipo de Estratégia:</label>
            <select id="strategy-type" v-model="newStrategy.type" required>
              <option value="">Selecione um tipo</option>
              <option value="IMAGE">Imagem</option>
              <option value="VIDEO">Vídeo</option>
              <option value="REDIRECT">Redirecionamento</option>
            </select>
          </div>

          <div v-if="newStrategy.type === 'IMAGE'" class="form-group">
            <label for="image-url">URL da Imagem:</label>
            <input
              id="image-url"
              v-model="newStrategy.data.url"
              type="text"
              required
            />
          </div>

          <div v-if="newStrategy.type === 'VIDEO'" class="form-group">
            <label for="video-url">URL do YouTube:</label>
            <input
              id="video-url"
              v-model="newStrategy.data.youtube_url"
              type="text"
              required
            />
          </div>

          <div v-if="newStrategy.type === 'REDIRECT'" class="form-group">
            <label for="redirect-url">URL de Redirecionamento:</label>
            <input
              id="redirect-url"
              v-model="newStrategy.data.url"
              type="text"
              required
            />
            <p v-if="newStrategyUrlInvalid" class="url-error">
              URL inválida. Use um endereço http(s) válido.
            </p>
            <label for="content-type">Tipo de Conteúdo:</label>
            <select v-model="newStrategy.data.contentType" required>
              <option value="">Selecione</option>
              <option
                v-for="ct in contentTypes"
                :key="ct.type"
                :value="ct.type"
              >
                {{ ct.text }}
              </option>
            </select>
          </div>

          <div class="modal-actions">
            <button
              type="button"
              class="cancel-btn"
              :disabled="savingStrategy"
              @click="closeModal"
            >
              Cancelar
            </button>
            <button
              type="submit"
              class="save-btn"
              :disabled="savingStrategy"
              data-test="save-strategy"
            >
              {{ savingStrategy ? "Salvando..." : "Salvar" }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getAllCampaigns,
  addStrategyToCampaign,
  getTrafficCount,
} from "@/services/api";
import { contentTypes } from "@/helpers";
import { normalizeRedirectUrl, buildTrackingUrl } from "@/helpers/url";
import QrCodeCard from "@/components/QrCodeCard.vue";

export default {
  name: "CampaignsDashboard",
  components: { QrCodeCard },
  data() {
    return {
      campaigns: [],
      contentTypes,
      loadingCampaigns: true,
      savingStrategy: false,
      showModal: false,
      showUtmModal: false,
      selectedCampaignId: null,
      selectedStrategy: null,
      newStrategy: {
        type: "",
        data: {},
      },
      trafficData: {}, // Real per-strategy counts, keyed by strategy _id
      utmParams: {
        utm_source: "",
        utm_medium: "",
        utm_campaign: "",
        utm_content: "",
        utm_term: "",
      },
      generatedUrl: "",
    };
  },
  computed: {
    newStrategyUrlInvalid() {
      return (
        this.newStrategy.type === "REDIRECT" &&
        Boolean(this.newStrategy.data.url) &&
        !normalizeRedirectUrl(this.newStrategy.data.url)
      );
    },
  },
  async mounted() {
    try {
      await this.loadCampaigns();
    } finally {
      // Only the initial load shows the full-page spinner; background
      // refreshes (after adding a strategy) keep the grid visible
      this.loadingCampaigns = false;
    }
  },
  methods: {
    async loadCampaigns() {
      try {
        const response = await getAllCampaigns();
        this.campaigns = response.campaigns || [];

        for (const campaign of this.campaigns) {
          for (const strategy of campaign.strategies) {
            getTrafficCount(campaign._id, { strategyId: strategy._id }).then(
              (count) => {
                this.trafficData[strategy._id] = count;
              },
            );
          }
        }
      } catch (error) {
        console.error("Error loading campaigns:", error);
      }
    },

    trafficFor(strategyId) {
      const count = this.trafficData[strategyId];
      return count === undefined ? "…" : count;
    },

    getStrategyTitle(type) {
      const titles = {
        IMAGE: "Imagem",
        VIDEO: "Vídeo",
        REDIRECT: "Redirecionamento",
        REWARD: "Recompensa",
      };
      return titles[type] || type;
    },

    strategyTarget(strategy) {
      return strategy.data?.youtube_url || strategy.data?.url || "—";
    },

    openLinkModal(campaign, strategy) {
      this.selectedCampaignId = campaign._id;
      this.selectedStrategy = strategy;
      this.showUtmModal = true;
      this.utmParams = {
        utm_source: "",
        utm_medium: "",
        utm_campaign: campaign.name,
        utm_content: strategy.type.toLowerCase(),
        utm_term: "",
      };
      // A plain link (no UTMs beyond the defaults) is useful immediately
      this.generateTrackingUrl();
    },

    editCampaign(campaignId) {
      this.$router.push(`/form?edit=${campaignId}`);
    },

    showAddStrategyModal(campaignId) {
      this.selectedCampaignId = campaignId;
      this.newStrategy = {
        type: "",
        data: {},
      };
      this.showModal = true;
    },

    closeModal() {
      this.showModal = false;
      this.selectedCampaignId = null;
      this.newStrategy = {
        type: "",
        data: {},
      };
    },

    async addNewStrategy() {
      // Re-entrancy guard: repeated clicks used to fire one PUT each,
      // creating duplicate strategies
      if (this.savingStrategy || this.newStrategyUrlInvalid) return;
      this.savingStrategy = true;
      try {
        await addStrategyToCampaign(this.selectedCampaignId, this.newStrategy);
        await this.loadCampaigns();
        this.closeModal();
        window.$message?.success("Estratégia adicionada com sucesso!");
      } catch (error) {
        console.error("Error adding strategy:", error);
        const apiError = error.response?.data?.error;
        window.$message?.error(
          apiError
            ? `Erro ao adicionar estratégia: ${apiError}`
            : "Erro ao adicionar estratégia. Tente novamente.",
        );
      } finally {
        this.savingStrategy = false;
      }
    },

    closeUtmModal() {
      this.showUtmModal = false;
      this.selectedCampaignId = null;
      this.selectedStrategy = null;
      this.generatedUrl = "";
      this.utmParams = {
        utm_source: "",
        utm_medium: "",
        utm_campaign: "",
        utm_content: "",
        utm_term: "",
      };
    },

    generateTrackingUrl() {
      this.generatedUrl = buildTrackingUrl(
        this.selectedCampaignId,
        this.selectedStrategy._id,
        this.utmParams,
      );
    },

    async copyGeneratedUrl() {
      if (!this.generatedUrl) return;
      try {
        await navigator.clipboard.writeText(this.generatedUrl);
        window.$message?.success("URL de rastreamento copiada!");
      } catch {
        window.$message?.error("Não foi possível copiar a URL.");
      }
    },
  },
};
</script>

<style scoped>
.dashboard {
  background-color: #1c1127;
  min-height: 100vh;
  padding: 20px;
}

.dashboard-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
}

.campaigns-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 30px;
  max-width: 1200px;
  margin: 0 auto;
}

.campaign-card {
  background-color: #4a3c56;
  border-radius: 17px;
  box-shadow: 4px 4px 4px 0px rgba(0, 0, 0, 0.25);
  padding: 20px;
  color: white;
}

.campaign-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.campaign-header h1 {
  margin: 0;
  font-size: 18px;
  font-family: Roboto, sans-serif;
}

.edit-btn {
  background: none;
  border: none;
  font-size: 16px;
  cursor: pointer;
  padding: 5px;
  border-radius: 3px;
  transition: background-color 0.2s;
}

.edit-btn:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.strategies-container {
  display: grid;
  gap: 15px;
  margin-bottom: 20px;
}

.strategy-tile {
  background-color: #1c1127;
  border-radius: 8px;
  padding: 15px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.strategy-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.strategy-header h3 {
  margin: 0;
  font-size: 16px;
  font-family: Roboto, sans-serif;
}

.strategy-destination {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  opacity: 0.8;
  margin-bottom: 12px;
}

.strategy-actions {
  display: flex;
  justify-content: flex-end;
}

.copy-url-btn {
  background-color: #56a5ee;
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 3px;
  font-size: 12px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.copy-url-btn:hover {
  background-color: #4a94d1;
}

.metric-badge {
  background-color: #beb3c7;
  border: 0.5px solid #1c1127;
  border-radius: 4px;
  padding: 6px 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 60px;
}

.metric-value {
  font-family: Roboto, sans-serif;
  font-weight: bold;
  color: #1c1127;
  font-size: 12px;
}

.metric-label {
  font-family: Roboto, sans-serif;
  color: #1c1127;
  font-size: 9px;
}

.add-strategy-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  background-color: #367934;
  color: white;
  border: none;
  padding: 15px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: bold;
  transition: background-color 0.2s;
  margin-top: 10px;
}

.add-strategy-btn:hover {
  background-color: #2d5f2a;
}

.plus-icon {
  font-size: 20px;
  font-weight: bold;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background-color: #4a3c56;
  border-radius: 10px;
  padding: 30px;
  max-width: 500px;
  width: 90%;
  color: white;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-content h3 {
  margin-top: 0;
  margin-bottom: 20px;
  color: white;
}

.form-group {
  margin-bottom: 15px;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
}

.form-group input,
.form-group select {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
  box-sizing: border-box;
}

.url-error {
  color: #ff8a8a;
  font-size: 12px;
  margin: 5px 0 0;
}

.modal-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 20px;
}

.cancel-btn {
  background-color: #683424;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 5px;
  cursor: pointer;
  font-weight: bold;
}

.cancel-btn:hover {
  background-color: #5a2c1f;
}

.save-btn {
  background-color: #367934;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 5px;
  cursor: pointer;
  font-weight: bold;
}

.save-btn:hover {
  background-color: #2d5f2a;
}

.generated-url {
  margin-top: 20px;
  padding: 15px;
  background-color: #1c1127;
  border-radius: 5px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.url-display {
  display: flex;
  gap: 10px;
  margin-top: 5px;
}

.url-display input {
  flex: 1;
  background-color: #4a3c56;
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.copy-btn {
  background-color: #56a5ee;
  color: white;
  border: none;
  padding: 8px 15px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
  white-space: nowrap;
}

.copy-btn:hover {
  background-color: #4a94d1;
}

.qr-wrapper {
  margin-top: 15px;
  display: flex;
  justify-content: center;
}

@media (max-width: 768px) {
  .campaigns-grid {
    grid-template-columns: 1fr;
  }
}
</style>
